// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge } from '@loa/ui-components';
import { PolicySignatoryStatus } from '../../types/signatoryStatus';
import './SignatoryStatusBadge.css';

type BadgeColor = 'grey' | 'orange' | 'yellow' | 'green' | 'red' | 'blue' | 'teal' | 'neutral' | 'purple';

type SignatoryStatusBadgeProps = Readonly<{
  status: string;
  /** Reserved — the badge always renders at the shared "sm" size today. */
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large';
  basic?: boolean;
}>;

const statusConfig: Record<string, { color: BadgeColor; text: string }> = {
  [PolicySignatoryStatus.DRAFT]: { color: 'grey', text: 'Draft' },
  [PolicySignatoryStatus.PENDING_REVIEW]: { color: 'yellow', text: 'Pending Review' },
  [PolicySignatoryStatus.CONFIRMED]: { color: 'green', text: 'Confirmed' },
  [PolicySignatoryStatus.PENDING_SIGNATURE]: { color: 'blue', text: 'Pending Signature' },
  [PolicySignatoryStatus.SIGNED]: { color: 'teal', text: 'Signed' },
  [PolicySignatoryStatus.FLAGGED]: { color: 'red', text: 'Flagged' },
  [PolicySignatoryStatus.EXCLUDED]: { color: 'grey', text: 'Excluded' },
};

export function SignatoryStatusBadge({ status, basic = false }: SignatoryStatusBadgeProps) {
  const config = statusConfig[status] || { color: 'grey' as BadgeColor, text: status };

  return (
    <Badge
      color={config.color}
      size="sm"
      variant={basic ? 'outlined' : 'filled'}
      aria-label={`Signatory status: ${config.text}`}
    >
      {config.text}
    </Badge>
  );
}
